/**
 * Auto-Normalizer
 *
 * Walks an API response tree, discovers entity objects via their `__type` field,
 * formats them using the registered formatter, flattens nested entities into a
 * shared `entities` bucket, and returns `{ entities, result }` — the same shape
 * the old normalizr-based normalizers produced, but without the normalizr library.
 *
 * Usage:
 *   const { entities, result } = autoNormalize(apiResponse);
 */

import { AnyObject, APIResponse } from "../models";
import { typeRegistry, storeRegistry, FlattenConfig, TypeRegistryEntry } from "./typeRegistry";
import camelize from "../helpers/camelize";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EntitiesMap = APIResponse["entities"];

export type AutoNormalizeResult<R = unknown> = {
  entities?: EntitiesMap;
  result: R;
};

// ---------------------------------------------------------------------------
// Batch-result detection
// ---------------------------------------------------------------------------

function isBatchResultList(arr: unknown[]): boolean {
  if (arr.length === 0) return false;
  const first = arr[0] as AnyObject | undefined;
  return typeof first?.success === "boolean" && Boolean(first.result || first.error);
}

/**
 * If a walked batch-result value is a single-key object with a primitive value
 * (i.e. an entity wrapper like `{ invitation: "id" }`), unwrap it to just the value.
 * Multi-key objects (like `{ keyword: "foo", assetsUpdated: 5 }`) pass through unchanged.
 */
function unwrapSingleEntityResult(value: unknown): unknown {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return value;
  const keys = Object.keys(value as AnyObject);
  if (keys.length === 1) {
    const inner = (value as AnyObject)[keys[0]];
    if (inner == null || typeof inner !== "object") return inner;
  }
  return value;
}

// ---------------------------------------------------------------------------
// Flattening — extracts nested entities from a formatted object
// ---------------------------------------------------------------------------

/**
 * Given a formatted entity object and its FlattenConfig, recursively extract
 * nested entities into the shared `entities` bucket. Nested entity values are
 * replaced in-place with their IDs.
 */
function applyFlatten(obj: AnyObject, config: FlattenConfig, entities: EntitiesMap): void {
  for (const [key, target] of Object.entries(config)) {
    if (!(key in obj) || obj[key] == null) continue;

    if (typeof target === "string") {
      // Direct entity reference → extract to the target store
      obj[key] = extractToStore(obj[key], target, entities);
    } else {
      // Sub-config: the property holds objects whose inner keys should be
      // flattened. Supports both arrays (e.g. Product.variants[].assets) and
      // record objects with arbitrary keys (e.g. Product.fields[*].assets).
      const container = obj[key];
      if (Array.isArray(container)) {
        obj[key] = container.map(item => {
          if (item && typeof item === "object") {
            applyFlatten(item as AnyObject, target, entities);
          }
          return item;
        });
      } else if (container && typeof container === "object") {
        for (const v of Object.values(container as AnyObject)) {
          if (v && typeof v === "object") {
            applyFlatten(v as AnyObject, target, entities);
          }
        }
      }
    }
  }
}

/**
 * Extract a single value (or array of values) into the entity store. Returns
 * the ID (or array of IDs) that should replace the original value.
 */
function extractToStore(
  value: unknown,
  storeKey: keyof EntitiesMap,
  entities: EntitiesMap
): unknown {
  if (value == null) return value;

  if (Array.isArray(value)) {
    return value.map(v => extractToStore(v, storeKey, entities)).filter(v => v != null);
  }

  // Already an ID (string or number) — nothing to extract
  if (typeof value !== "object") return value;

  const obj = value as AnyObject;
  const storeConfig = storeRegistry[storeKey];
  if (!storeConfig) return value;

  // Recursively flatten this entity's own nested entities first
  if (storeConfig.flatten && Object.keys(storeConfig.flatten).length > 0) {
    applyFlatten(obj, storeConfig.flatten, entities);
  }

  // Store the entity and return its ID
  const id = storeConfig.getId(obj);
  entities[storeKey] ??= {};
  entities[storeKey][id] = obj;

  return id;
}

// ---------------------------------------------------------------------------
// Tree walker — discovers __type-annotated objects in the response.
//
// The incoming tree has already been deep-camelCased via `camelize` at the
// entry points, so walking concerns itself only with entity discovery and
// normalization — no key reshaping here.
//
// The walker runs in two modes:
//   - normalize=true  (envelope mode): `__type` entities with a `storeKey`
//     are extracted into entity buckets and replaced with their ID. This is
//     the top-level behavior and also applies when descending through plain
//     (non-entity) objects.
//   - normalize=false (inside-entity mode): `__type` descendants are formatted
//     and left inline. The parent entity's `flatten` config is the only way
//     to extract children into entity buckets. This mode is entered when we
//     recurse into a formatted entity's own values, so inlined entities like
//     `space.access` stay inline.
//
// `entities` is optional: when absent, no normalization happens regardless
// of mode — the tree is formatted in-place (used by `autoFormat`).
// ---------------------------------------------------------------------------

function processEntity(
  raw: AnyObject,
  entry: TypeRegistryEntry,
  entities: EntitiesMap | undefined,
  normalize: boolean
): unknown {
  // Re-entry guard: if this entity has already been formatted (shared sub-tree,
  // repeat call), skip the formatter call itself. We still re-walk children so
  // that a subtree encountered first as a flatten target (normalize=true) and
  // later as an inlined sibling (normalize=false) — or vice versa — applies
  // the correct mode the second time around.
  let formatted: AnyObject;
  if (raw.__formatted) {
    formatted = raw;
  } else {
    // Keys are already fully camelCased by the upfront `camelize` pass.
    // Formatters only need to overlay derived fields, defaults, or
    // restructured children.
    formatted = entry.formatter ? (entry.formatter(raw) as AnyObject) : raw;
    formatted.__formatted = true;
  }

  // Recurse into values in inside-entity mode so nested __type descendants
  // format-only; the parent's `flatten` is what pulls listed children out.
  for (const [key, val] of Object.entries(formatted)) {
    formatted[key] = walk(val, entities, false);
  }

  if (entities) {
    if (entry.flatten && Object.keys(entry.flatten).length > 0) {
      applyFlatten(formatted, entry.flatten, entities);
    }

    if (normalize && entry.storeKey) {
      const id = entry.getId(formatted);
      entities[entry.storeKey] ??= {};
      entities[entry.storeKey][id] = formatted;
      return id;
    }
  }

  return formatted;
}

/**
 * Recursively walk a value from the (already camelCased) API response.
 * Entity objects (those with `__type`) are formatted; when `entities` is
 * provided and `normalize` is true, entities with a `storeKey` are also
 * collected there and replaced with their ID. Keys in `IGNORE_KEYS` are
 * copied through untouched (no deeper recursion).
 */
function walk(value: unknown, entities: EntitiesMap | undefined, normalize: boolean): unknown {
  if (value == null || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    // Detect batch-result format: [{ success, result, error }]
    if (isBatchResultList(value)) {
      return value.map(entry => {
        const e = entry as AnyObject;
        const result: AnyObject = { ...e };
        if (e.result != null) {
          result.result = unwrapSingleEntityResult(walk(e.result, entities, normalize));
        }
        if (e.error != null && typeof e.error === "object") {
          result.error = walk(e.error, entities, normalize);
        }
        return result;
      });
    }
    return value.map(v => walk(v, entities, normalize));
  }

  const obj = value as AnyObject;

  // Entity object — format, flatten (if entities), store (if normalize + storeKey)
  if (typeof obj.__type === "string") {
    const entry = typeRegistry[obj.__type as string];
    if (entry) {
      return processEntity(obj, entry, entities, normalize);
    }
    // Unknown __type — treat as plain object
  }

  // Plain object — recurse into values. Plain objects preserve the caller's
  // mode: descending through an envelope object keeps envelope semantics
  // (entities at any depth still normalize).
  for (const [key, val] of Object.entries(obj)) {
    obj[key] = walk(val, entities, normalize);
  }
  return obj;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function autoNormalize<R = unknown>(responseData: unknown): AutoNormalizeResult<R> {
  const entities: EntitiesMap = {};
  const converted = responseData == null ? responseData : camelize(responseData as AnyObject);
  const result = walk(converted, entities, true);
  return { result: result as R, entities };
}

export function autoFormat<R = unknown>(responseData: unknown): { result: R } {
  const converted = responseData == null ? responseData : camelize(responseData as AnyObject);
  const result = walk(converted, undefined, true);
  return { result: result as R };
}
